import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Mic, Music, Paintbrush, FileText, Wand2, Radio, Calendar, Megaphone,
  Bot, Layers, Clock, Settings, ArrowLeft, BarChart3, Activity, Library
} from "lucide-react";
import { useAuth, BACKEND_URL } from "../context/AuthContext";
import { AssistantChat } from "../components/ai/AssistantChat";
import { PromptHistory } from "../components/ai/PromptHistory";
import { AISettingsPanel } from "../components/ai/AISettingsPanel";
import { JobQueue } from "../components/ai/JobQueue";
import { PodcastGenerator } from "../components/ai/PodcastGenerator";
import { PodcastLibrary } from "../components/ai/PodcastLibrary";
import { AIInsights } from "../components/ai/AIInsights";
import { AIHealth } from "../components/ai/AIHealth";
import { BroadcastScheduler } from "../components/ai/BroadcastScheduler";
import { AIRadioHost } from "../components/ai/AIRadioHost";
import { PlaylistIntelligence } from "../components/ai/PlaylistIntelligence";

const AI_TOOLS = [
  { id: 'podcast_generation', icon: Mic, label: 'AI Podcast Generator', desc: 'Generate full podcast episodes with AI voices, intros, and segments.', color: 'from-violet-500/20 to-fuchsia-500/20', border: 'border-violet-500/30', badge: 'Active', active: true, tab: 'podcast' },
  { id: 'dj_generation', icon: Radio, label: 'AI Radio Host', desc: 'Generate show intros, station IDs, music breaks, weather updates, sponsor spots.', color: 'from-cyan-500/20 to-blue-500/20', border: 'border-cyan-500/30', badge: 'Active', active: true, tab: 'radio_host' },
  { id: 'voice_synthesis', icon: Wand2, label: 'Voice Synthesis', desc: 'Clone and generate custom voice tracks for station IDs and promos.', color: 'from-amber-500/20 to-orange-500/20', border: 'border-amber-500/30', badge: 'Coming Soon' },
  { id: 'artwork_creation', icon: Paintbrush, label: 'AI Artwork Creator', desc: 'Generate album art, station logos, and social media graphics.', color: 'from-pink-500/20 to-rose-500/20', border: 'border-pink-500/30', badge: 'Coming Soon' },
  { id: 'show_notes', icon: FileText, label: 'Show Notes Writer', desc: 'Auto-generate show notes, descriptions, and SEO-optimized text.', color: 'from-emerald-500/20 to-teal-500/20', border: 'border-emerald-500/30', badge: 'Coming Soon' },
  { id: 'music_analysis', icon: Music, label: 'Playlist Intelligence', desc: 'Recommend songs by genre, mood, BPM, and time of day.', color: 'from-indigo-500/20 to-purple-500/20', border: 'border-indigo-500/30', badge: 'Active', active: true, tab: 'playlist' },
  { id: 'content_scheduling', icon: Calendar, label: 'Broadcast Scheduler', desc: 'Schedule AI podcasts to one or more stations with recurring runs.', color: 'from-sky-500/20 to-cyan-500/20', border: 'border-sky-500/30', badge: 'Active', active: true, tab: 'scheduler' },
  { id: 'promo_generation', icon: Megaphone, label: 'Promo Generator', desc: 'Create promotional audio drops and sweepers with AI narration.', color: 'from-red-500/20 to-orange-500/20', border: 'border-red-500/30', badge: 'Coming Soon' },
];

const TABS = [
  { id: 'dashboard', label: 'Dashboard', icon: Layers },
  { id: 'podcast', label: 'Podcast Generator', icon: Mic },
  { id: 'library', label: 'Library', icon: Library },
  { id: 'scheduler', label: 'Broadcast Scheduler', icon: Calendar },
  { id: 'radio_host', label: 'AI Radio Host', icon: Radio },
  { id: 'playlist', label: 'Playlist Intelligence', icon: Music },
  { id: 'assistant', label: 'Assistant', icon: Bot },
  { id: 'jobs', label: 'Job Queue', icon: Layers },
  { id: 'insights', label: 'Insights', icon: BarChart3 },
  { id: 'health', label: 'Health', icon: Activity },
  { id: 'history', label: 'History', icon: Clock },
  { id: 'settings', label: 'Settings', icon: Settings },
];

export default function AIStudio() {
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const authToken = token || localStorage.getItem('token');

  if (authLoading) return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-white/20 border-t-cyan-500 rounded-full animate-spin" />
    </div>
  );

  if (!user) { navigate('/'); return null; }

  return (
    <div className="min-h-screen bg-gray-950 p-6" data-testid="ai-studio">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500/30 to-violet-500/30 flex items-center justify-center border border-cyan-500/20">
              <Bot className="w-5 h-5 text-cyan-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">AI Studio</h1>
              <p className="text-white/40 text-xs">Intelligence center for your radio station</p>
            </div>
          </div>
          <button onClick={() => navigate('/dashboard')} className="flex items-center gap-2 text-white/50 hover:text-white transition text-sm">
            <ArrowLeft className="w-4 h-4" /> Dashboard
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-1 mb-6 bg-white/[0.03] rounded-xl p-1 overflow-x-auto" data-testid="ai-studio-tabs">
          {TABS.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center justify-center gap-1.5 py-2.5 px-3 rounded-lg text-xs font-medium transition whitespace-nowrap ${
                activeTab === tab.id ? 'bg-cyan-500/15 text-cyan-400 border border-cyan-500/20' : 'text-white/40 hover:text-white/70'
              }`} data-testid={`ai-tab-${tab.id}`}>
              <tab.icon className="w-3.5 h-3.5" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Dashboard Tab — Tool Cards */}
        {activeTab === 'dashboard' && (
          <div className="space-y-6" data-testid="ai-dashboard">
            {/* Tool Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {AI_TOOLS.map(tool => (
                <button key={tool.id}
                  onClick={() => setActiveTab(tool.active ? tool.tab : 'jobs')}
                  className={`text-left p-5 rounded-2xl border bg-gradient-to-br ${tool.color} ${tool.border} hover:scale-[1.02] transition-all group ${tool.active ? 'ring-1 ring-violet-400/30' : ''}`}
                  data-testid={`ai-tool-card-${tool.id}`}>
                  <div className="flex items-start justify-between mb-3">
                    <tool.icon className="w-7 h-7 text-white/80 group-hover:text-white transition" />
                    <span className={`text-[9px] px-2 py-0.5 rounded-full font-medium ${
                      tool.active ? 'bg-green-500/20 text-green-400' : 'bg-white/10 text-white/50'
                    }`}>{tool.badge}</span>
                  </div>
                  <h4 className="text-white font-semibold text-sm mb-1">{tool.label}</h4>
                  <p className="text-white/40 text-xs leading-relaxed">{tool.desc}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Podcast Generator Tab */}
        {activeTab === 'podcast' && <PodcastGenerator authToken={authToken} />}

        {/* Podcast Library Tab */}
        {activeTab === 'library' && <PodcastLibrary authToken={authToken} />}

        {/* Broadcast Scheduler Tab */}
        {activeTab === 'scheduler' && <BroadcastScheduler authToken={authToken} />}

        {/* AI Radio Host Tab */}
        {activeTab === 'radio_host' && <AIRadioHost authToken={authToken} />}

        {/* Playlist Intelligence Tab */}
        {activeTab === 'playlist' && <PlaylistIntelligence authToken={authToken} />}

        {/* Assistant Tab */}
        {activeTab === 'assistant' && <AssistantChat authToken={authToken} />}

        {/* Job Queue Tab */}
        {activeTab === 'jobs' && <JobQueue authToken={authToken} />}

        {/* Insights Tab */}
        {activeTab === 'insights' && <AIInsights authToken={authToken} />}

        {/* Health Tab */}
        {activeTab === 'health' && <AIHealth authToken={authToken} />}

        {/* History Tab */}
        {activeTab === 'history' && <PromptHistory authToken={authToken} />}

        {/* Settings Tab */}
        {activeTab === 'settings' && <AISettingsPanel authToken={authToken} />}
      </div>
    </div>
  );
}
